import { createApi } from '@reduxjs/toolkit/query/react';

import { fetchBaseQueryWithAuth } from '../../fetchBaseQuery';
import { runtimeConfig } from '../../../config/runtimeConfig';
import {
  WebhookEndpoint,
  WebhookEventDataType,
  GetWebhookEventsApiResponse,
  GetWebhookEventsQuery,
  GetWebhookEventsResponse,
  CreateWebhookBody,
  UpdateWebhookBody,
} from '../../../pages/MerchantWebhooks/types/types';
import { normalizeWebhookEvent, normalizeWebhookEventsResponse } from './transformers';

export type { GetWebhookEventsResponse };

export const webhooksApi = createApi({
  reducerPath: 'webhooksApi',
  tagTypes: ['Webhook', 'WebhookEvent'],
  baseQuery: fetchBaseQueryWithAuth(runtimeConfig.webhooksApiBaseUrl || ''),
  endpoints: (builder) => ({
    getWebhooks: builder.query<WebhookEndpoint[], string>({
      query: (merchantId) => ({
        url: `/v1/${merchantId}/subscriptions`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ subscriptionId }) => ({
                type: 'Webhook' as const,
                id: subscriptionId,
              })),
              { type: 'Webhook', id: 'LIST' },
            ]
          : [{ type: 'Webhook', id: 'LIST' }],
    }),

    createWebhook: builder.mutation<WebhookEndpoint, CreateWebhookBody>({
      query: ({ merchantId, ...body }) => ({
        url: `/v1/${merchantId}/subscriptions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Webhook', id: 'LIST' }],
    }),

    updateWebhook: builder.mutation<WebhookEndpoint, UpdateWebhookBody>({
      query: ({ subscriptionId, merchantId, ...body }) => ({
        url: `/v1/${merchantId}/subscriptions/${subscriptionId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { subscriptionId }) => [
        { type: 'Webhook', id: subscriptionId },
        { type: 'Webhook', id: 'LIST' },
      ],
    }),

    deleteWebhook: builder.mutation<void, { subscriptionId: string; merchantId: string }>({
      query: ({ subscriptionId, merchantId }) => ({
        url: `/v1/${merchantId}/subscriptions/${subscriptionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { subscriptionId }) => [
        { type: 'Webhook', id: subscriptionId },
        { type: 'Webhook', id: 'LIST' },
      ],
    }),

    getWebhookEvents: builder.query<GetWebhookEventsResponse, GetWebhookEventsQuery>({
      keepUnusedDataFor: 1800,
      query: ({
        merchantId,
        pageSize = 10,
        pageNumber = 0,
        sortBy,
        sortField,
        initialSendTo,
        initialSendFrom,
        lastAttemptTo,
        lastAttemptFrom,
        statuses,
        eventTypes,
      }) => {
        const params: Record<string, string | number | string[]> = {
          pageSize,
          pageNumber,
        };

        if (sortBy) {
          params.sortBy = sortBy;
        }
        if (sortField) {
          params.sortField = sortField;
        }
        if (initialSendTo) {
          params.initialSendTo = initialSendTo;
        }
        if (initialSendFrom) {
          params.initialSendFrom = initialSendFrom;
        }
        if (lastAttemptTo) {
          params.lastAttemptTo = lastAttemptTo;
        }
        if (lastAttemptFrom) {
          params.lastAttemptFrom = lastAttemptFrom;
        }
        if (statuses && statuses.length > 0) {
          params.statuses = statuses;
        }
        if (eventTypes && eventTypes.length > 0) {
          params.eventTypes = eventTypes;
        }

        return {
          url: `/v1/${merchantId}/events`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: GetWebhookEventsApiResponse) =>
        normalizeWebhookEventsResponse(response),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ eventId }) => ({
                type: 'WebhookEvent' as const,
                id: eventId,
              })),
              { type: 'WebhookEvent', id: 'LIST' },
            ]
          : [{ type: 'WebhookEvent', id: 'LIST' }],
    }),

    getWebhookEvent: builder.query<WebhookEventDataType, { eventId: string; merchantId: string }>({
      query: ({ eventId, merchantId }) => ({
        url: `/v1/${merchantId}/events/${eventId}`,
        method: 'GET',
      }),
      transformResponse: normalizeWebhookEvent,
      providesTags: (_result, _error, { eventId }) => [{ type: 'WebhookEvent', id: eventId }],
    }),

    retryWebhookEvent: builder.mutation<
      void,
      { eventId: string; merchantId: string; useCurrentSubscription?: boolean }
    >({
      query: ({ eventId, merchantId, useCurrentSubscription = false }) => ({
        url: `/v1/${merchantId}/events/${eventId}/retry`,
        method: 'POST',
        body: { useCurrentSubscription },
      }),
    }),
  }),
});

export const {
  useGetWebhooksQuery,
  useLazyGetWebhooksQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useGetWebhookEventsQuery,
  useLazyGetWebhookEventsQuery,
  useGetWebhookEventQuery,
  useLazyGetWebhookEventQuery,
  useRetryWebhookEventMutation,
} = webhooksApi;
